import _ from "lodash";
import L from "lodash/fp";
import inject from "scripts/ioc/inject";
import Backbone from "backbone";
import BaseView from "scripts/views/baseView";
import MediaPlaylistView from "scripts/views/mediaPlaylistView";
import HybridMediaCollection from "scripts/collections/hybridMediaCollection";
import MediaModel from "scripts/models/mediaModel";
import templateAudioShadowBox from "templates/audioShadowBox.hbs";

const chan = Backbone.Radio.channel;

class AudioShadowBoxView extends BaseView {
  get template() {
    return templateAudioShadowBox;
  }

  get defaultPlayerOptions() {
    return {
      skin: "five",
      fallback: false,
      autostart: true,
      width: 670,
      height: 40,
    };
  }

  constructor(
    options,
    relatedContentService = inject("relatedContentService"),
  ) {
    super(options);

    this.relatedContentService = relatedContentService;

    this.model = new Backbone.Model();
  }

  render() {
    this.setupShadowBox();

    return this;
  }

  setupShadowBox() {
    $.fancybox.open({
      helpers: {
        overlay: {
          locked: true,
        },
      },
      content: this.template(this.model.toJSON()),
      autoSize: false,
      width: this.defaultPlayerOptions.width,
      autoHeight: true,
      scrolling: "no",
      afterShow: this.setupPlayer.bind(this),
      beforeClose: this.removePlayer.bind(this),
      onUpdate: this.onShadowBoxUpdate.bind(this),
    });
  }

  async setupPlayer() {
    $.fancybox.showLoading();

    // Needed to correct z-index issue with iOS devices (UIWebView)
    $(".fancybox-wrap.fancybox-mobile").css("transform", "translate3d(0,0,0)");

    const playerOptions = _.clone(this.defaultPlayerOptions);
    const playlist = await this.generatePlaylist();

    console.log("JWPlayer playlist: %O", playlist);

    if (playlist.length) {
      playerOptions.playlist = playlist;
      this.initPlaylist();
    } else {
      playerOptions.sources = this.jwPlayerSources(
        this.model.get("audioFormats"),
      );
    }

    jwplayer("bb-audio-player")
      .setup(playerOptions)
      .on("playlistItem", this.onPlaylistItemChange.bind(this))
      .on("firstFrame", this.onFirstFrame.bind(this))
      .on("ready", this.onPlayerReady.bind(this, playlist));

    console.log("JWPlayer initialized");
  }

  initPlaylist() {
    this.playlistView = this.addSubView(
      new MediaPlaylistView({
        collection: this.relatedAudioCollection,
      }),
    );

    this.playlistView
      .htmlOf($("#bb-audio-shadow-box-playlist-region"))
      .render();

    this.playlistView.on(
      "playlistTrackClicked",
      this.onClickPlaylistTrack,
      this,
    );
  }

  onClickPlaylistTrack(contentModel) {
    const indexToPlay = _.findIndex(jwplayer().getPlaylist(), {
      mediaid: contentModel.get("id"),
    });
    const currentIndex = jwplayer().getPlaylistIndex();

    if (indexToPlay !== currentIndex) {
      jwplayer().playlistItem(indexToPlay);
    }
  }

  /**
   * Triggered when playback begins.
   */
  onFirstFrame() {
    const currentPlaylistIndex = jwplayer().getPlaylistIndex();
    const playlistItem = jwplayer().getPlaylist()[currentPlaylistIndex];
    const mediaId = playlistItem.mediaid || this.model.get("id");

    this.sendCounterEvent(mediaId);
  }

  onPlaylistItemChange(playlistItem) {
    this.updateTitle(playlistItem.item.title);
    this.initQualityBtn();

    chan("playlist").trigger("playlistItemChange", playlistItem.item.mediaid);
  }

  onPlayerReady(playlist) {
    $.fancybox.update();

    if (playlist.length) {
      const indexToPlay = _.findIndex(playlist, {
        mediaid: this.model.get("id"),
      });
      const currentIndex = jwplayer().getPlaylistIndex();

      if (indexToPlay !== currentIndex) {
        jwplayer().playlistItem(indexToPlay);
      }
    }

    $.fancybox.hideLoading();

    $("#bb-audio-player").focus();
  }

  /**
   * JW Player version 7 in the audio player mode (height set to 40 or
   * below) does not show the option to toggle audio quality by default.
   */
  initQualityBtn() {
    const currentPlaylistIndex = jwplayer().getPlaylistIndex();
    const qualityCount = jwplayer().getPlaylist()[currentPlaylistIndex].sources
      .length;
    const $qualityBtn = $("#bb-audio-player .jw-icon-hd");

    if (qualityCount > 1) {
      $qualityBtn.css("display", "inline-block");
    } else {
      $qualityBtn.css("display", "none");
    }
  }

  /**
   * Generate JW Player sources object from API audio formats.
   */
  jwPlayerSources(formats) {
    const bitRateOrder = ["64k", "192k"];

    const getBitRateOrderValue = bitRate => {
      const index = bitRateOrder.indexOf(bitRate);
      return index === -1 ? bitRateOrder.length : index;
    };

    return L.pipe(
      L.map(format => ({
        file: format.url,
        label: format.bitRate,
      })),
      sources =>
        sources.sort(
          (a, b) =>
            getBitRateOrderValue(a.label) - getBitRateOrderValue(b.label),
        ),
    )(formats);
  }

  removeShadowBox() {
    if ($.fancybox.isOpen) {
      $.fancybox.close(true);
      console.log("FancyBox closed");
    }
  }

  removePlayer() {
    if (this.isPlayerInitialized()) {
      jwplayer().remove();
      console.log("JWPlayer removed");
    }
  }

  onShadowBoxUpdate() {
    this.autoSizePlayer();
  }

  /**
   * When JWPlayer is in audio mode (height of 40 or lower), the width
   * has to be fixed - percentage width and aspect ratio does not work
   * correctly.
   */
  autoSizePlayer() {
    const width = $(".fancybox-outer").width();

    if (this.isPlayerInitialized()) {
      jwplayer().resize(width, this.defaultPlayerOptions.height);
    }
  }

  isPlayerInitialized() {
    return jwplayer().id === "bb-audio-player";
  }

  async generatePlaylist() {
    this.relatedAudioCollection = await this.fetchRelated();

    return [].concat(
      this.relatedAudioCollection.map(this.getPlaylistItem.bind(this)),
    );
  }

  getPlaylistItem(audioModel) {
    const title = audioModel.get("title");
    const subtitle = audioModel.get("subtitle");

    return {
      mediaid: audioModel.get("id"),
      sources: this.jwPlayerSources(audioModel.get("audioFormats")),
      title: subtitle ? `${title}: ${subtitle}` : `${title}`,
    };
  }

  fetchRelated() {
    return this.relatedContentService.fetchSiblingsForContentType(
      new HybridMediaCollection(),
      this.model,
      new MediaModel(),
    );
  }

  updateTitle(title) {
    $("#bb-audio-shadow-box .bb-audio-shadow-box-title").text(title);
  }

  sendCounterEvent(contentMediaId) {
    chan("tracking").trigger("content:open", {
      mediaId: contentMediaId,
      contentType: this.model.get("type"),
    });
  }

  close() {
    this.removeShadowBox(); // Callback will cleanup JWPlayer

    super.close();
  }
}

export default AudioShadowBoxView;
